import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { domains, categories } from '@/mocks/data';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function CategoryPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const currentCategory = categories.find((c) => c.slug === slug);
  const filteredDomains = domains.filter((d) => {
    if (slug && d.category !== slug) return false;
    if (statusFilter === 'online' && d.status !== 'online') return false;
    if (statusFilter === 'offline' && d.status !== 'offline') return false;
    return true;
  });

  const breadcrumbItems = [
    { label: t('common.breadcrumbHome'), href: '/' },
    { label: t('nav.categories'), href: '/category/video' },
    { label: currentCategory?.name || slug || '' },
  ];

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: currentCategory ? `${currentCategory.name} - 日韩在线永久域名` : '分类浏览 - 日韩在线永久域名',
    description: currentCategory?.description || '',
    url: `https://japanlink.github.io/category/${slug}`,
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: currentCategory ? `${currentCategory.name} - 日韩在线永久域名` : '分类浏览',
    description: currentCategory?.description || '',
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>

      <main className="pt-20 md:pt-24">
        <Breadcrumb items={breadcrumbItems} />

        {/* Category Header */}
        <section className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3 font-heading">
              {currentCategory?.name || t('category.title')}
            </h1>
            <p className="text-sm md:text-base text-foreground-500">
              {currentCategory?.description || t('category.subtitle')}
            </p>

            {/* Category Tabs */}
            <div className="mt-8 flex items-center gap-2 flex-wrap">
              <Link
                to="/category/video"
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  !slug || slug === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {t('category.allCategories')}
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Status Filter */}
            <div className="mt-4 flex items-center gap-2">
              <span className="text-xs text-foreground-400">{t('category.filterByStatus')}：</span>
              {['all', 'online', 'offline'].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    statusFilter === status
                      ? 'bg-secondary-100 text-secondary-700'
                      : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                  }`}
                >
                  {status === 'all' ? t('category.allStatus') : status === 'online' ? t('category.online') : t('category.offline')}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Domain List */}
        <section className="w-full px-4 md:px-6 pb-14 md:pb-20">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-500">
                共找到 <strong className="text-foreground-950">{filteredDomains.length}</strong> {t('category.domainCount')}
              </p>
            </div>

            {filteredDomains.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {filteredDomains.map((domain) => (
                  <Link
                    key={domain.id}
                    to={`/article/${domain.id}`}
                    className="group bg-background-50 rounded-xl p-5 border border-background-200/60 hover:border-primary-300 transition-all duration-300 no-underline cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                        <i className="ri-link text-accent-600 text-lg"></i>
                      </div>
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                          domain.status === 'online'
                            ? 'bg-accent-100 text-accent-700'
                            : 'bg-foreground-100 text-foreground-500'
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                            domain.status === 'online' ? 'bg-accent-500' : 'bg-foreground-400'
                          }`}
                        ></span>
                        {domain.status === 'online' ? t('common.online') : t('common.offline')}
                      </span>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors font-heading">
                      {domain.name}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed mb-3">
                      {domain.description}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {domain.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 bg-background-100 rounded text-xs text-foreground-500"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-background-100 flex items-center justify-between">
                      <span className="text-xs text-foreground-400">
                        更新于 {domain.updateTime}
                      </span>
                      <span className="text-xs text-foreground-400">
                        <i className="ri-eye-line mr-1"></i>{(domain.visits / 10000).toFixed(1)}万
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">{t('category.noDomains')}</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}